<template>
    <NuxtLink v-slot="{navigate, href}" :to="to" custom>
        <div
            class="resource-card"
            :class="{'resource-card--collection': documentType === 'collection'}"
            :data-testid="baseDataTestid"
            @click="navigate"
        >
            <div class="resource-card__image">
                <div class="resource-card__image__wrapper">
                    <slot name="image-overlay" />
                    <PrismicImage v-if="image" :field="image" class="resource-card__image__thumbnail" :imgix-params="{w: 800}" />
                </div>
                <div class="resource-card__image__hover">
                    <span>{{ imageHoverText }}</span>
                </div>
            </div>
            <div class="resource-card__body">
                <div v-if="category || date" class="resource-card__category-wrapper">
                    <ResourceCategory
                        :resource-categories="resourceCategories"
                        :category="category"
                        :data-testid="`${baseDataTestid}--category-pill`"
                        @expand-click="onCategoryExpanded"
                        @click.stop
                    />
                    <div v-if="date" class="resource-card__date" :data-testid="`${baseDataTestid}--date`">
                        <span>{{ date }}</span>
                    </div>
                </div>
                <div
                    v-if="categoryPills?.expanded"
                    class="resource-card__category-pills--expanded"
                    :data-testid="`${baseDataTestid}--category-pills-expanded`"
                    @click.stop
                >
                    <CategoryPill v-for="item in categoryPills?.items" :key="item.category" :category="item.category" :label="item.label" />
                </div>

                <a :href="href">
                    <h3 class="resource-card__title" :data-testid="`${baseDataTestid}--title`">{{ title }}</h3>
                </a>
                <div class="resource-card__excerpt-wrapper" :data-testid="`${baseDataTestid}--excerpt`">
                    <PrismicRichText class="resource-card__excerpt" :field="excerpt" />
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script lang="ts" setup>
    import {computed, ref} from 'vue';
    import type {ImageField, KeyTextField, RichTextField, SelectField} from '@prismicio/types';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {ResourceCategoryDocument} from '@prismicio-types';
    import {useTranslation} from '#imports';
    import CategoryPill from '~/components/CategoryPill.vue';
    import ResourceCategory from '~/components/ResourceCategory.vue';

    const props = defineProps<{
        documentType: 'article' | 'successStory' | 'event' | 'guide' | 'collection';
        image?: ImageField;
        title: KeyTextField;
        excerpt: RichTextField;
        to: string;
        isUpcomingEvent?: boolean;
        resourceCategories?: {resourceCategory: ResourceCategoryDocument}[] | undefined;
        category?: SelectField<
            | 'data'
            | 'techAndProduct'
            | 'marketingAndCommunication'
            | 'designAndCreation'
            | 'businessConsultingAndStrategy'
            | 'businessFunctions'
            | 'industrialEngineering'
        >;
        date?: string;
        baseDataTestid?: string;
    }>();

    const {t} = useTranslation();

    const imageHoverText = computed(() => {
        if (props.documentType === 'article' || props.documentType === 'successStory') return t('resources.ctas.read');
        if (props.documentType === 'event' && props.isUpcomingEvent) return t('resources.ctas.register');
        if (props.documentType === 'event' && !props.isUpcomingEvent) return t('resources.ctas.see');

        return t('resources.ctas.discover');
    });

    type CategoryPills = {
        expanded: boolean;
        items: Array<{category: string; label: string}>;
    };

    const categoryPills = ref<CategoryPills>();
    function onCategoryExpanded(payload: CategoryPills) {
        categoryPills.value = payload;
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .resource-card {
        display: flex;
        height: 100%;
        flex-direction: column;
        background: var(--joy-color-background-default);
        border-radius: var(--joy-core-radius-4);
        padding: var(--joy-core-spacing-4) var(--joy-core-spacing-4) var(--joy-core-spacing-8) var(--joy-core-spacing-4);
        border: 1px solid var(--joy-color-neutral-30);
        cursor: pointer;

        @include mq.screen_sm(min) {
            &:hover {
                .resource-card__image__hover {
                    opacity: 1;
                }
            }
        }

        @include mq.screen_sm(min) {
            border: none;

            &:hover {
                box-shadow: var(--joy-core-elevation-2);

                img {
                    filter: blur(3px);
                }
            }
        }

        &__image {
            width: 100%;
            aspect-ratio: 16 / 9;
            max-height: 290px;
            overflow: hidden;
            border-radius: var(--joy-core-radius-4);
            position: relative;

            &__wrapper {
                position: relative;
                margin: -3px;
            }

            &__thumbnail {
                width: 100%;
                height: 100%;
                object-fit: cover;
                position: relative;
                display: block;
            }

            &__hover {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotateZ(-6deg);
                border-radius: 40px;
                background-color: var(--joy-color-primary-50);
                z-index: 2;
                opacity: 0;
                transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);

                span {
                    display: inline-block;
                    padding: var(--joy-core-spacing-2) var(--joy-core-spacing-5) var(--joy-core-spacing-0) var(--joy-core-spacing-5);
                    font-family: var(--joy-font-family-title);
                    font-size: var(--joy-font-size-primary-600);
                    line-height: var(--joy-line-height-small);
                    color: var(--joy-color-neutral-0);
                }
            }
        }

        &__title,
        &__excerpt {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
        }

        &__title {
            margin-top: var(--joy-core-spacing-3);
            font-size: var(--joy-font-size-primary-800);
        }

        &__excerpt {
            margin-top: var(--joy-core-spacing-2);

            p {
                color: var(--joy-color-neutral-40);
            }
        }

        &__body {
            margin-top: var(--joy-core-spacing-4);

            a {
                text-decoration: none !important;
            }
        }

        &__category-wrapper {
            display: flex;
            align-items: center;
            min-height: 28px;
        }

        &__date {
            display: flex;
            flex-shrink: 0;
            align-items: center;
            gap: var(--joy-core-spacing-1);
            margin-left: var(--joy-core-spacing-3);
        }

        &__category-pills {
            &--expanded {
                display: flex;
                gap: var(--joy-core-spacing-2);
                margin-top: var(--joy-core-spacing-2);
                flex-wrap: wrap;
                width: 100%;
            }
        }

        &--collection &__image {
            aspect-ratio: unset;
        }
    }
</style>
